import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const nextSteps = [
    {
      step: '01',
      title: 'Choose Your Calculator',
      description: 'Select the calculator that best matches your current priorities and challenges.',
      icon: 'Calculator'
    },
    {
      step: '02',
      title: 'Complete Assessment',
      description: 'Answer questions about your organization, goals, and current capabilities.',
      icon: 'CheckSquare'
    },
    {
      step: '03',
      title: 'Get Your Report',
      description: 'Receive detailed analysis with ROI projections and implementation roadmap.',
      icon: 'FileText'
    },
    {
      step: '04',
      title: 'Book Consultation',
      description: 'Optional expert consultation to validate findings and refine your strategy.',
      icon: 'Calendar'
    }
  ];

  const resources = [
    {
      title: 'Data Maturity Assessment',
      description: 'Comprehensive evaluation of your data capabilities',
      link: '/data-maturity-calculator-lead-generation-assessment',
      icon: 'BarChart3'
    },
    {
      title: 'Expert Consultation',
      description: 'One-on-one strategy session with our data experts',
      link: '/contact-multi-channel-engagement-hub',
      icon: 'Users'
    },
    {
      title: 'Knowledge Hub',
      description: 'Access frameworks, templates, and best practices',
      link: '/knowledge-hub-thought-leadership-center',
      icon: 'BookOpen'
    }
  ];

  return (
    <section className="py-20 lg:py-24 bg-gradient-to-br from-surface to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary rounded-full px-4 py-2 mb-6">
            <Icon name="Rocket" size={16} />
            <span className="text-sm font-medium">Get Started</span>
          </div>
          <h2 className="text-section-title text-primary mb-6">
            Ready to Calculate Your ROI?
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
            Take the first step toward data-driven decision making. Our calculators provide 
            the insights you need to build compelling business cases and secure stakeholder buy-in.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button
              variant="default"
              size="lg"
              iconName="Calculator"
              iconPosition="left"
              asChild
              className="bg-secondary hover:bg-secondary/90 hover-lift"
            >
              <Link to="/data-maturity-calculator-lead-generation-assessment">
                Start ROI Calculator
              </Link>
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageCircle"
              iconPosition="left"
              asChild
              className="hover-lift"
            >
              <Link to="/contact-multi-channel-engagement-hub">
                Speak with Expert
              </Link>
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-8 text-muted-foreground text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} />
              <span>GDPR Compliant</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Lock" size={16} />
              <span>Secure Data Handling</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Award" size={16} />
              <span>Industry Certified</span>
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="mb-20">
          <h3 className="text-2xl font-semibold text-primary text-center mb-12">
            How It Works
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {nextSteps?.map((step, index) => (
              <div key={index} className="text-center group">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary/20 transition-colors duration-300">
                    <Icon name={step?.icon} size={24} color="var(--color-secondary)" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-secondary text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {step?.step}
                  </div>
                  {index < nextSteps?.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-border -translate-x-1/2"></div>
                  )}
                </div>
                <h4 className="font-semibold text-primary mb-2">
                  {step?.title}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step?.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Resources */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-subtle border border-border">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-semibold text-primary mb-4">
              Explore Additional Resources
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Complement your calculator results with our comprehensive suite of assessment tools and expert guidance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {resources?.map((resource, index) => (
              <Link
                key={index}
                to={resource?.link}
                className="group p-6 rounded-xl border border-border hover:border-secondary/50 hover:shadow-elevation transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-secondary/20 transition-colors duration-300">
                  <Icon name={resource?.icon} size={24} color="var(--color-secondary)" />
                </div>
                <h4 className="font-semibold text-primary mb-2 group-hover:text-secondary transition-colors duration-300">
                  {resource?.title}
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  {resource?.description}
                </p>
                <div className="flex items-center space-x-2 text-secondary text-sm font-medium">
                  <span>Learn more</span>
                  <Icon name="ArrowRight" size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl p-8 lg:p-12 text-white">
            <h3 className="text-2xl font-semibold mb-4">
              Questions About Our Calculators?
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Our team is here to help you get the most value from your assessment. 
              Schedule a brief call to discuss your specific requirements.
            </p>
            <Button
              variant="outline"
              size="lg"
              iconName="Phone"
              iconPosition="left"
              asChild
              className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 hover-lift"
            >
              <Link to="/contact-multi-channel-engagement-hub">
                Schedule Call
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;